/**
 * <s-review-pages> — Numbered pagination over Judge.me's All Reviews widget.
 *
 * Judge.me's All Reviews widget only offers "load more" (button or scroll). This component shows
 * its reviews a page at a time with numbered pages. When a shopper pages past the reviews already
 * loaded, it clicks Judge.me's own (visually hidden) load-more button and waits for the next batch.
 * Sorting/filtering by Judge.me replaces the list; the component notices and returns to page 1.
 *
 * Attributes:
 *   per-page            — Reviews per page (default 9).
 *   total               — Total review count from Judge.me, used for the page count while more can load.
 *   data-label          — aria-label for the pagination nav.
 *   data-previous       — Previous button label.
 *   data-next           — Next button label.
 *   data-page-template  — aria-label per page button, "{{ page }}" placeholder.
 *   data-loading        — Text announced while the next batch loads.
 *   data-load-more-text — Judge.me's load-more button text (fallback for finding it).
 */
class SReviewPages extends HTMLElement {
  #perPage = 9;
  #page = 1;
  #pending = null;
  #observer = null;
  #nav = null;
  #status = null;
  #frame = 0;
  #signature = '';

  connectedCallback() {
    this.#perPage = Math.max(1, parseInt(this.getAttribute('per-page'), 10) || 9);

    this.#nav = document.createElement('nav');
    this.#nav.className = 'review-pages';
    this.#nav.setAttribute('aria-label', this.dataset.label || 'Review pages');
    this.#nav.addEventListener('click', this.#onClick);

    this.#status = document.createElement('p');
    this.#status.className = 'sr-only';
    this.#status.setAttribute('aria-live', 'polite');

    this.append(this.#nav, this.#status);

    this.#observer = new MutationObserver((records) => {
      const own = (node) => node === this.#nav || node === this.#status || this.#nav.contains(node);
      const relevant = records.some((record) => {
        if (own(record.target)) return false;
        const nodes = [...record.addedNodes, ...record.removedNodes];
        return nodes.length === 0 || nodes.some((node) => !own(node));
      });
      if (relevant) this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#nav?.removeEventListener('click', this.#onClick);
  }

  get #reviews() {
    return [...this.querySelectorAll('.jdgm-rev')].filter((review) => !review.closest('.jdgm-form-wrapper'));
  }

  get #loadMore() {
    const candidates = this.querySelectorAll('[class*="load-more"], [class*="load_more"], [class*="loadmore"], button, a');
    const text = (this.dataset.loadMoreText || '').trim().toLowerCase();
    for (const element of candidates) {
      if (this.#nav.contains(element)) continue;
      const className = typeof element.className === 'string' ? element.className : '';
      const isLoadMore = /load[-_]?more/i.test(className) || (text && element.textContent.trim().toLowerCase() === text);
      if (isLoadMore && (element.tagName === 'A' || element.tagName === 'BUTTON' || element.getAttribute('role') === 'button' || /load[-_]?more/i.test(className))) {
        element.classList.add('review-pages__jm-load-more');
        return element;
      }
    }
    return null;
  }

  get #canLoadMore() {
    const button = this.#loadMore;
    if (!button) return false;
    const style = getComputedStyle(button);
    return style.display !== 'none' && style.visibility !== 'hidden' && !button.hasAttribute('disabled');
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const reviews = this.#reviews;
    const firstId = reviews[0]?.dataset.reviewId || '';
    const signature = `${firstId}`;

    // Judge.me replaced the list (sort or star filter): back to page 1.
    if (this.#signature && signature !== this.#signature && !this.#pending) this.#page = 1;
    this.#signature = signature;

    if (this.#pending && reviews.length >= this.#pending.needed) {
      this.#page = this.#pending.page;
      this.#pending = null;
      this.#status.textContent = '';
      this.#scrollToTop();
    } else if (this.#pending && !this.#canLoadMore && reviews.length <= this.#pending.loadedBefore) {
      // Nothing more came back: settle on the last page we have.
      this.#pending = null;
      this.#status.textContent = '';
    }

    this.#render(reviews);
  }

  #pageCount(reviews) {
    const loadedPages = Math.ceil(reviews.length / this.#perPage) || 1;
    if (!this.#canLoadMore) return loadedPages;
    const total = parseInt(this.getAttribute('total'), 10) || 0;
    return Math.max(loadedPages + 1, Math.ceil(total / this.#perPage));
  }

  #render(reviews) {
    const pages = this.#pageCount(reviews);
    const loadedPages = Math.ceil(reviews.length / this.#perPage) || 1;
    if (this.#page > pages) this.#page = pages;
    if (this.#page > loadedPages && !this.#pending) this.#page = loadedPages;

    const start = (this.#page - 1) * this.#perPage;
    const end = start + this.#perPage;
    reviews.forEach((review, index) => {
      review.classList.toggle('is-paged-out', index < start || index >= end);
    });

    this.#nav.hidden = pages <= 1;
    this.#nav.replaceChildren(...this.#buttons(pages));
  }

  #buttons(pages) {
    const current = this.#pending ? this.#pending.page : this.#page;
    const items = [];
    items.push(this.#button(this.dataset.previous || 'Previous', current - 1, current <= 1, 'review-pages__step'));

    for (const entry of this.#pageList(current, pages)) {
      if (entry === 'gap') {
        const gap = document.createElement('span');
        gap.className = 'review-pages__gap';
        gap.setAttribute('aria-hidden', 'true');
        gap.textContent = '…';
        items.push(gap);
      } else {
        const button = this.#button(String(entry), entry, false, 'review-pages__number');
        const template = this.dataset.pageTemplate || 'Page {{ page }}';
        button.setAttribute('aria-label', template.replace('{{ page }}', entry));
        if (entry === current) button.setAttribute('aria-current', 'page');
        items.push(button);
      }
    }

    items.push(this.#button(this.dataset.next || 'Next', current + 1, current >= pages, 'review-pages__step'));
    return items;
  }

  #pageList(current, pages) {
    if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);
    const list = [1];
    const from = Math.max(2, current - 1);
    const to = Math.min(pages - 1, current + 1);
    if (from > 2) list.push('gap');
    for (let page = from; page <= to; page += 1) list.push(page);
    if (to < pages - 1) list.push('gap');
    list.push(pages);
    return list;
  }

  #button(label, page, disabled, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.dataset.page = String(page);
    button.disabled = disabled;
    return button;
  }

  #onClick = (event) => {
    const button = event.target.closest('button[data-page]');
    if (!button || button.disabled) return;
    this.#goTo(parseInt(button.dataset.page, 10));
  };

  #goTo(page) {
    const reviews = this.#reviews;
    const needed = (page - 1) * this.#perPage + 1;

    if (reviews.length >= needed || !this.#canLoadMore) {
      this.#pending = null;
      this.#page = page;
      this.#render(reviews);
      this.#scrollToTop();
      return;
    }

    this.#pending = { page, needed, loadedBefore: reviews.length };
    this.#status.textContent = this.dataset.loading || 'Loading more reviews';
    this.#render(reviews);
    this.#loadMore?.click();
  }

  #scrollToTop() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = this.getBoundingClientRect().top + window.scrollY - 96;
    if (top < window.scrollY) window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('s-review-pages')) customElements.define('s-review-pages', SReviewPages);
